import { useEffect } from 'react';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useMemo } from 'react';

interface UseAccommodationsConsoleListingsArgs {
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
}

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, variant, onError, onSuccess, isInitialLoad } = args;

  const isViewingCouplesPicks = variant === 'couple';

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({ state });
  const { isFetchingListings, listings, cachedListings, error } = useRegionalHotels({ state, isInitialLoad });

  const consolidatedListings = useMemo(() => {
    /**
     * Keep track of joyPlaceIds from event accommodations to avoid duplicates in the list
     */
    const eventAccommodationsFromCatalogByJoyPlaceIdSet = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as Array<string>);

    return [
      // Prioritize event accommodations
      ...eventAccommodations,
      // Remove listings that are already chosen by the event
      ...listings.filter(listing => !!listing.joyPlaceId && !eventAccommodationsFromCatalogByJoyPlaceIdSet.has(listing.joyPlaceId))
    ];
  }, [eventAccommodations, listings]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    } else if (!isFetchingListings && listings.length > 0) {
      onSuccess?.();
    }
  }, [error, isFetchingListings, listings.length, onError, onSuccess]);

  return {
    /**
     * Can be used to retain maps points while loading new data
     */
    cachedListings,
    listings: consolidatedListings,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    isFetchingListingPrices: false,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null //error
  };
};
